import type { ReactNode } from 'react';

export interface ItemBarra {
  chave: string;
  rotulo: string;
  valor: number;
  /** Linha de apoio sob o numero (ex.: "38% do plantel"). */
  sub?: string | undefined;
}

/**
 * Barras horizontais de UMA serie (uma cor so, Aco): composicao de um todo
 * por categoria. Ordena do maior para o menor e junta o que passa de `maximo`
 * em "Outros", que nunca e clicavel.
 *
 * Com `onEscolher`, cada barra vira um botao que filtra a pagina por aquela
 * categoria; a escolhida fica cheia e as outras recuam. O numero vai escrito
 * ao lado de toda barra: a cor nunca e o unico portador do valor.
 */
export function BarrasHorizontais({
  itens,
  rotulo,
  formatar,
  maximo = 8,
  onEscolher,
  ativo,
  vazio,
}: {
  itens: readonly ItemBarra[];
  /** Nome do grafico para o leitor de tela. */
  rotulo: string;
  formatar: (v: number) => string;
  maximo?: number;
  onEscolher?: (chave: string) => void;
  ativo?: string | null;
  vazio?: ReactNode;
}) {
  const ordenados = [...itens].sort((a, b) => b.valor - a.valor || a.rotulo.localeCompare(b.rotulo, 'pt-BR'));
  let lista: Array<ItemBarra & { outros?: number }> = ordenados;
  if (ordenados.length > maximo) {
    const resto = ordenados.slice(maximo - 1);
    lista = [
      ...ordenados.slice(0, maximo - 1),
      {
        chave: '__outros',
        rotulo: `Outros ${resto.length}`,
        valor: resto.reduce((t, i) => t + i.valor, 0),
        outros: resto.length,
      },
    ];
  }
  const max = Math.max(0, ...lista.map((i) => i.valor));
  if (max === 0)
    return <p className="px-2 py-8 text-center text-sm text-apagado">{vazio ?? 'Nada a comparar no recorte.'}</p>;

  return (
    <ul aria-label={rotulo} className="flex flex-col gap-0.5">
      {lista.map((i) => {
        // Zero e sem barra: um risco minimo diria que ha algo.
        const largura = i.valor > 0 ? `${Math.max(1, (i.valor / max) * 100).toFixed(1)}%` : '0%';
        const recua = ativo != null && ativo !== i.chave;
        const conteudo = (
          <>
            <span className={`truncate text-[13px] ${i.outros ? 'text-apagado' : 'text-texto'}`}>{i.rotulo}</span>
            <span className="flex h-3 items-center">
              <span
                className={`block h-3 rounded-[2px_4px_4px_2px] transition-[width,opacity] duration-300 ${
                  i.outros ? 'bg-apagado/40' : 'bg-serie'
                } ${recua ? 'opacity-35' : ''}`}
                style={{ width: largura }}
              />
            </span>
            <span className="numero min-w-16 text-right text-[12.5px] font-semibold whitespace-nowrap">
              {formatar(i.valor)}
              {i.sub && <small className="block text-[11px] font-normal text-apagado">{i.sub}</small>}
            </span>
          </>
        );
        const classe =
          'grid w-full grid-cols-[minmax(84px,170px)_minmax(0,1fr)_auto] items-center gap-3 rounded-[9px] px-2 py-1.5 text-left';
        return (
          <li key={i.chave}>
            {onEscolher && !i.outros ? (
              <button
                type="button"
                aria-pressed={ativo === i.chave}
                onClick={() => onEscolher(i.chave)}
                title={`Filtrar: ${i.rotulo}`}
                className={`${classe} min-h-0 hover:bg-superficie-2`}
              >
                {conteudo}
              </button>
            ) : (
              <div className={classe}>{conteudo}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
